import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Plus, Trash2, Loader2, Package, RotateCcw, IndianRupee, FileText, User, FileSpreadsheet } from "lucide-react";

const formatCurrency = (v) => {
  if (!v && v !== 0) return "-";
  return "₹" + Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + "/-";
};

export default function Returns() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [searchOrder, setSearchOrder] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Return form
  const [returnItems, setReturnItems] = useState([]);
  const [remark, setRemark] = useState("");
  const [condition, setCondition] = useState("");
  const [receivedBy, setReceivedBy] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [broughtBy, setBroughtBy] = useState("");
  const [amountAdjustment, setAmountAdjustment] = useState(0);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));

  // Detail view
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [postingId, setPostingId] = useState(null);

  const fetchReturns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/returns`);
      setReturns(res.data || []);
    } catch (e) { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const searchOrderByNumber = async () => {
    if (!searchOrder.trim()) return;
    setSearching(true);
    setOrderData(null);
    try {
      const res = await axios.get(`${API}/orders`);
      const orders = res.data || [];
      const found = orders.find(o => o.order_number?.toUpperCase() === searchOrder.trim().toUpperCase());
      if (found) {
        setOrderData(found);
        // Pre-fill return items from order items
        setReturnItems((found.items || []).map(item => ({
          grade: item.grade || "",
          sku: item.sku || "",
          original_qty: item.qty || 0,
          qty: 0,
          rate: item.rate || 0,
          amount: 0,
        })));
      } else {
        toast.error(`Order ${searchOrder} not found`);
      }
    } catch (e) { toast.error("Failed to search order"); }
    finally { setSearching(false); }
  };

  const updateReturnItemQty = (idx, qty) => {
    setReturnItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const q = parseFloat(qty) || 0;
      return { ...item, qty: q, amount: q * item.rate };
    }));
  };

  const NINE_KG_GRADES = ['JH', 'SJH', 'JJH', 'W150', 'W180', '180', 'A180', 'PG'];
  const getPackingKg = (grade) => NINE_KG_GRADES.includes((grade || '').toUpperCase().trim()) ? 9 : 10;
  const totalReturnQty = returnItems.reduce((s, i) => s + (i.qty || 0), 0);
  const totalReturnKg = returnItems.reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i.grade)), 0);
  const totalReturnValue = returnItems.reduce((s, i) => s + (i.amount || 0), 0);
  const paymentTerms = (orderData?.payment_terms || "").toUpperCase().trim();
  const isDiscountApplicable = paymentTerms === "ADV" || paymentTerms === "DDAP" || paymentTerms === "DDAP RECVD";
  const discountPerKg = isDiscountApplicable ? (orderData?.discount_per_kg || 0) : 0;
  const discountAdjustment = isDiscountApplicable ? returnItems.reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i.grade) * discountPerKg), 0) : 0;
  const netCreditValue = totalReturnValue - discountAdjustment + (parseFloat(amountAdjustment) || 0);

  const createReturn = async () => {
    const validItems = returnItems.filter(i => i.qty > 0);
    if (validItems.length === 0) { toast.error("Enter return qty for at least one item"); return; }
    if (!remark.trim()) { toast.error("Return remark is required"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/returns`, {
        order_number: orderData.order_number,
        items: validItems.map(i => ({ grade: i.grade, sku: i.sku, qty: i.qty, rate: i.rate, amount: i.amount })),
        remark,
        condition,
        received_by: receivedBy,
        vehicle_number: vehicleNumber,
        brought_by: broughtBy,
        amount_adjustment: parseFloat(amountAdjustment) || 0,
        return_date: returnDate,
      });
      toast.success(`Return ${res.data.return_number} created. Credit: ${formatCurrency(res.data.net_credit_value)}`);
      setShowCreate(false);
      resetForm();
      fetchReturns();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create return"); }
    finally { setSaving(false); }
  };

  const deleteReturn = async (id) => {
    if (!window.confirm("Delete this return and its credit receipt?")) return;
    try {
      await axios.delete(`${API}/returns/${id}`);
      toast.success("Return deleted");
      fetchReturns();
      if (selectedReturn?.id === id) setSelectedReturn(null);
    } catch (e) { toast.error("Failed to delete return"); }
  };

  const postReturnToSheet = async (id) => {
    setPostingId(id);
    try {
      const res = await axios.post(`${API}/returns/${id}/post-to-sheet`);
      toast.success(res.data?.message || "Return posted to Google Sheets");
      fetchReturns();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to post to sheet"); }
    finally { setPostingId(null); }
  };

  const resetForm = () => {
    setSearchOrder("");
    setOrderData(null);
    setReturnItems([]);
    setRemark("");
    setCondition("");
    setReceivedBy("");
    setVehicleNumber("");
    setBroughtBy("");
    setAmountAdjustment(0);
    setReturnDate(new Date().toISOString().slice(0, 10));
  };

  return (
    <div className="p-4 max-w-full">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Returns</h1>
        <Button size="sm" onClick={() => { resetForm(); setShowCreate(true); }} className="gap-1 h-8" data-testid="new-return-btn">
          <Plus className="w-3.5 h-3.5" /> New Return
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-4">
        {/* Returns List */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><RotateCcw className="w-4 h-4" /> All Returns ({returns.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : returns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No returns recorded yet</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30">
                    <TableHead className="text-xs">Return #</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Order #</TableHead>
                    <TableHead className="text-xs">Customer</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Credit Value</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Received By</TableHead>
                    <TableHead className="text-xs w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {returns.map(r => (
                    <TableRow key={r.id} className={`cursor-pointer ${selectedReturn?.id === r.id ? 'bg-primary/10' : 'hover:bg-secondary/30'}`} onClick={() => setSelectedReturn(r)}>
                      <TableCell className="text-xs font-mono font-semibold text-blue-700">{r.return_number}</TableCell>
                      <TableCell className="text-xs">{r.return_date}</TableCell>
                      <TableCell className="text-xs font-mono">{r.order_number}</TableCell>
                      <TableCell className="text-xs truncate max-w-[120px]">{r.dispatch_name || r.customer_name}</TableCell>
                      <TableCell className="text-xs text-right font-bold">{r.return_qty}</TableCell>
                      <TableCell className="text-xs text-right font-bold text-green-700">{formatCurrency(r.net_credit_value)}</TableCell>
                      <TableCell className="text-xs">
                        {r.status === 'confirmed' ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-[9px]">Confirmed</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px]">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{r.received_by_username || r.received_by || '-'}</TableCell>
                      <TableCell className="text-xs">
                        <div className="flex gap-0.5">
                          <Button size="sm" variant="ghost" className={`h-5 w-5 p-0 ${r.posted_to_sheet ? 'text-green-600 hover:text-green-800' : 'text-red-500 hover:text-red-700'}`} onClick={(e) => { e.stopPropagation(); postReturnToSheet(r.id); }} title={r.posted_to_sheet ? "Posted to Google Sheets" : "Not posted - Click to post"} disabled={postingId === r.id} data-testid={`post-return-${r.id}`}>
                            {postingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={(e) => { e.stopPropagation(); deleteReturn(r.id); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Return Detail Panel */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Return Details</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {selectedReturn ? (
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Return #:</span> <span className="font-bold text-blue-700">{selectedReturn.return_number}</span></div>
                  <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{selectedReturn.return_date}</span></div>
                  <div><span className="text-muted-foreground">Order #:</span> <span className="font-mono font-semibold">{selectedReturn.order_number}</span></div>
                  <div><span className="text-muted-foreground">Dispatch #:</span> <span className="font-mono">{selectedReturn.dispatch_number || '-'}</span></div>
                  <div className="col-span-2"><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedReturn.dispatch_name || selectedReturn.customer_name}</span></div>
                  <div><span className="text-muted-foreground">Received By:</span> <span className="font-medium">{selectedReturn.received_by_username || selectedReturn.received_by || '-'}</span></div>
                  <div><span className="text-muted-foreground">Condition:</span> <span className="font-medium">{selectedReturn.condition || '-'}</span></div>
                  <div><span className="text-muted-foreground">Vehicle No:</span> <span className="font-medium">{selectedReturn.vehicle_number || '-'}</span></div>
                  <div><span className="text-muted-foreground">Brought By:</span> <span className="font-medium">{selectedReturn.brought_by || '-'}</span></div>
                  <div><span className="text-muted-foreground">Status:</span> {selectedReturn.status === 'confirmed' ? <Badge className="bg-green-100 text-green-800 border-green-300 text-[9px] ml-1">Confirmed</Badge> : <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] ml-1">Pending</Badge>}</div>
                  {selectedReturn.confirmed_by_username && <div><span className="text-muted-foreground">Confirmed By:</span> <span className="font-medium">{selectedReturn.confirmed_by_username}</span></div>}
                </div>

                <div className="border-t pt-2">
                  <p className="font-semibold mb-1">Return Items</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px]">Grade</TableHead>
                        <TableHead className="text-[10px]">SKU</TableHead>
                        <TableHead className="text-[10px] text-right">Qty</TableHead>
                        <TableHead className="text-[10px] text-right">Rate</TableHead>
                        <TableHead className="text-[10px] text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(selectedReturn.items || []).map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-[10px] font-medium">{item.grade}</TableCell>
                          <TableCell className="text-[10px]">{item.sku}</TableCell>
                          <TableCell className="text-[10px] text-right">{item.qty}</TableCell>
                          <TableCell className="text-[10px] text-right">{formatCurrency(item.rate)}</TableCell>
                          <TableCell className="text-[10px] text-right font-bold">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="border-t pt-2 space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Return Value:</span> <span className="font-bold">{formatCurrency(selectedReturn.return_value)}</span></div>
                  {selectedReturn.discount_per_kg > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Discount Adj (₹{selectedReturn.discount_per_kg}/kg x {selectedReturn.return_qty}):</span> <span className="font-bold text-red-600">-{formatCurrency(selectedReturn.discount_adjustment)}</span></div>
                  )}
                  {selectedReturn.amount_adjustment && selectedReturn.amount_adjustment !== 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Amount Adjustment:</span> <span className={`font-bold ${selectedReturn.amount_adjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>{selectedReturn.amount_adjustment > 0 ? '+' : ''}{formatCurrency(selectedReturn.amount_adjustment)}</span></div>
                  )}
                  <div className="flex justify-between border-t pt-1"><span className="font-semibold">Net Credit:</span> <span className="font-bold text-green-700 text-sm">{formatCurrency(selectedReturn.net_credit_value)}</span></div>
                </div>

                {selectedReturn.remark && (
                  <div className="border-t pt-2">
                    <span className="text-muted-foreground">Remark:</span>
                    <p className="mt-0.5 text-xs bg-secondary/30 rounded p-2">{selectedReturn.remark}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">Select a return to view details</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Return Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) resetForm(); setShowCreate(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-4">
          <DialogHeader className="pb-2 border-b">
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-5 h-5" /> New Return</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Step 1: Search Order */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground mb-1 block">Order Number</label>
              <div className="flex gap-2">
                <Input placeholder="Enter order number (e.g., E03211)" value={searchOrder} onChange={e => setSearchOrder(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchOrderByNumber()} className="h-8 text-sm" data-testid="return-order-search" />
                <Button size="sm" onClick={searchOrderByNumber} disabled={searching} className="h-8 gap-1">
                  {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Search
                </Button>
              </div>
            </div>

            {/* Step 2: Order Summary */}
            {orderData && (
              <>
                <Card className="bg-secondary/20">
                  <CardContent className="p-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Order:</span> <span className="font-mono font-bold">{orderData.order_number}</span></div>
                      <div><span className="text-muted-foreground">Dispatch #:</span> <span className="font-mono">{orderData.dispatch_number || '-'}</span></div>
                      <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{orderData.dispatch_name || orderData.customer_name}</span></div>
                      <div><span className="text-muted-foreground">Status:</span> <Badge variant="outline" className="text-[9px] ml-1">{orderData.status?.toUpperCase()}</Badge></div>
                      <div><span className="text-muted-foreground">Total Qty:</span> <span className="font-bold">{orderData.total_qty}</span></div>
                      <div><span className="text-muted-foreground">Total Value:</span> <span className="font-bold">{formatCurrency(orderData.total_value)}</span></div>
                      <div><span className="text-muted-foreground">Discount/Kg:</span> <span className="font-bold text-red-600">₹{orderData.discount_per_kg || 0}</span></div>
                      <div><span className="text-muted-foreground">Payment:</span> <span className="font-medium">{orderData.payment_terms || '-'}</span></div>
                    </div>
                  </CardContent>
                </Card>

                {/* Step 3: Return Items */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground mb-1 block">Return Items (enter qty to return)</label>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/30">
                        <TableHead className="text-xs">Grade</TableHead>
                        <TableHead className="text-xs">SKU</TableHead>
                        <TableHead className="text-xs text-right">Ordered Qty</TableHead>
                        <TableHead className="text-xs text-right w-[90px]">Return Qty</TableHead>
                        <TableHead className="text-xs text-right">Rate</TableHead>
                        <TableHead className="text-xs text-right">Return Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnItems.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs font-medium">{item.grade}</TableCell>
                          <TableCell className="text-xs">{item.sku}</TableCell>
                          <TableCell className="text-xs text-right">{item.original_qty}</TableCell>
                          <TableCell className="text-xs text-right">
                            <Input type="number" min="0" max={item.original_qty} value={item.qty || ''} onChange={e => updateReturnItemQty(idx, e.target.value)} className="h-7 w-[80px] text-xs text-right ml-auto" data-testid={`return-qty-${idx}`} />
                          </TableCell>
                          <TableCell className="text-xs text-right">{formatCurrency(item.rate)}</TableCell>
                          <TableCell className="text-xs text-right font-bold">{item.qty > 0 ? formatCurrency(item.amount) : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Value Summary */}
                <Card className="bg-blue-50/50 border-blue-200">
                  <CardContent className="p-3">
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between"><span>Return Qty:</span> <span className="font-bold">{totalReturnQty} packs ({totalReturnKg} kg)</span></div>
                      <div className="flex justify-between"><span>Return Value:</span> <span className="font-bold">{formatCurrency(totalReturnValue)}</span></div>
                      {isDiscountApplicable && discountPerKg > 0 && (
                        <div className="flex justify-between text-red-600"><span>Discount (₹{discountPerKg}/kg x {totalReturnKg} kg):</span> <span className="font-bold">-{formatCurrency(discountAdjustment)}</span></div>
                      )}
                      {!isDiscountApplicable && (
                        <div className="flex justify-between text-muted-foreground"><span>Discount:</span> <span className="text-xs">N/A ({orderData?.payment_terms || '-'} payment)</span></div>
                      )}
                      <div className="flex justify-between items-center">
                        <span>Amount Adjustment (+/-):</span>
                        <Input type="number" value={amountAdjustment || ''} onChange={e => setAmountAdjustment(e.target.value)} className="h-6 w-[120px] text-xs text-right" placeholder="0" data-testid="return-amount-adjustment" />
                      </div>
                      <div className="flex justify-between border-t pt-1 text-sm"><span className="font-semibold">Net Credit to Customer:</span> <span className="font-bold text-green-700">{formatCurrency(netCreditValue)}</span></div>
                    </div>
                  </CardContent>
                </Card>

                {/* Step 4: Additional Details */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Return Date</label>
                    <Input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Condition</label>
                    <Select value={condition} onValueChange={setCondition}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select condition" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Damaged" className="text-xs">Damaged</SelectItem>
                        <SelectItem value="Quality Issue" className="text-xs">Quality Issue</SelectItem>
                        <SelectItem value="Wrong Grade" className="text-xs">Wrong Grade</SelectItem>
                        <SelectItem value="Excess Qty" className="text-xs">Excess Qty</SelectItem>
                        <SelectItem value="Customer Request" className="text-xs">Customer Request</SelectItem>
                        <SelectItem value="Other" className="text-xs">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Vehicle Number</label>
                    <Input placeholder="Vehicle number" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} className="h-8 text-xs" data-testid="return-vehicle-number" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Person Who Brought Return</label>
                    <Input placeholder="Person who brought the return" value={broughtBy} onChange={e => setBroughtBy(e.target.value)} className="h-8 text-xs" data-testid="return-brought-by" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Received By</label>
                    <Input placeholder="Name of person who received return" value={receivedBy} onChange={e => setReceivedBy(e.target.value)} className="h-8 text-xs" data-testid="return-received-by" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">Remark *</label>
                    <Input placeholder="Return reason / notes" value={remark} onChange={e => setRemark(e.target.value)} className="h-8 text-xs" data-testid="return-remark" />
                  </div>
                </div>
              </>
            )}
          </div>

          {orderData && (
            <DialogFooter className="pt-3 border-t">
              <Button variant="outline" onClick={() => { resetForm(); setShowCreate(false); }}>Cancel</Button>
              <Button onClick={createReturn} disabled={saving || totalReturnQty === 0} className="gap-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Create Return (Pending Confirmation)
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
